import { SkeletonLoader } from "@/components/ui/skeleton";

interface SellerUnitOneSkeletonProps {
	className?: string;
	isInModal?: boolean;
}

export function SellerUnitOneSkeleton({ className = "", isInModal = false }: SellerUnitOneSkeletonProps) {
	return (
		<div className={`@container ${className} min-h-screen bg-white max-w-7xl mx-auto`}>
			<div className="px-0 @4xl:px-4">
				{/* Breadcrumbs skeleton - only show if not in modal */}
				{!isInModal && (
					<div className="mt-6 mb-4 px-2 @4xl:px-0">
						<div className="flex items-center gap-2">
							<SkeletonLoader height="16px" width="60px" />
							<span className="text-gray-400">/</span>
							<SkeletonLoader height="16px" width="80px" />
							<span className="text-gray-400">/</span>
							<SkeletonLoader height="16px" width="120px" />
						</div>
					</div>
				)}

				{/* Title skeleton */}
				<div className="mt-4 mb-6 px-2 @4xl:px-0 space-y-3">
					<SkeletonLoader height="32px" width="70%" />
					<SkeletonLoader height="20px" width="50%" />
					<div className="flex items-center gap-4">
						<SkeletonLoader height="24px" width="120px" />
						<SkeletonLoader height="20px" width="80px" />
					</div>
				</div>
			</div>

			<div className="px-0 @4xl:px-4">
				{/* Mobile Layout Skeleton */}
				<div className="@4xl:hidden mt-4">
					{/* Gallery skeleton */}
					<div className="mb-3 h-[300px] -mx-0 @4xl:-mx-4">
						<SkeletonLoader height="300px" width="100%" className="rounded-none" />
					</div>

					{/* Overview Section - Mobile */}
					<div className="px-2 space-y-4">
						<SkeletonLoader height="28px" width="40%" />
						<SkeletonLoader height="20px" width="60%" />
						<div className="grid grid-cols-2 gap-4">
							<SkeletonLoader height="60px" width="100%" />
							<SkeletonLoader height="60px" width="100%" />
						</div>
						<SkeletonLoader height="48px" width="100%" />
					</div>
				</div>

				{/* Desktop Layout Skeleton */}
				<div className="hidden @4xl:block mt-4">
					<div className="grid grid-cols-1 @4xl:grid-cols-3 gap-8 mb-8">
						{/* Gallery Section */}
						<div className="@4xl:col-span-2 min-h-[500px]">
							<SkeletonLoader height="500px" width="100%" />
						</div>

						{/* Overview Section - Desktop Sidebar */}
						<div className="@4xl:col-span-1 space-y-4">
							<SkeletonLoader height="28px" width="60%" />
							<SkeletonLoader height="20px" width="80%" />
							<div className="space-y-3">
								<SkeletonLoader height="60px" width="100%" />
								<SkeletonLoader height="60px" width="100%" />
								<SkeletonLoader height="48px" width="100%" />
							</div>
						</div>
					</div>
				</div>

				{/* Floor Plan skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="120px" className="mb-4" />
					<div className="mb-3 h-[300px] @4xl:h-[800px] -mx-4">
						<SkeletonLoader height="100%" width="100%" className="rounded-none" />
					</div>
				</div>

				{/* Details Tables skeleton */}
				<div className="grid grid-cols-1 @4xl:grid-cols-2 gap-8 mb-8 px-2 @4xl:px-0">
					<div className="space-y-4">
						<SkeletonLoader height="24px" width="140px" />
						<div className="space-y-3">
							{[...Array(6)].map((_, index) => (
								<div key={index} className="flex justify-between">
									<SkeletonLoader height="16px" width="40%" />
									<SkeletonLoader height="16px" width="30%" />
								</div>
							))}
						</div>
					</div>
					<div className="space-y-4">
						<SkeletonLoader height="24px" width="120px" />
						<div className="space-y-3">
							{[...Array(6)].map((_, index) => (
								<div key={index} className="flex justify-between">
									<SkeletonLoader height="16px" width="40%" />
									<SkeletonLoader height="16px" width="30%" />
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Amenities skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="100px" className="mb-4" />
					<div className="grid grid-cols-2 @4xl:grid-cols-4 gap-4">
						{[...Array(8)].map((_, index) => (
							<div key={index} className="flex items-center gap-2">
								<SkeletonLoader height="20px" width="20px" />
								<SkeletonLoader height="16px" width="80px" />
							</div>
						))}
					</div>
				</div>

				{/* Additional Features skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="160px" className="mb-4" />
					<div className="space-y-2">
						{[...Array(4)].map((_, index) => (
							<SkeletonLoader key={index} height="16px" width={`${[70, 85, 75, 80][index]}%`} />
						))}
					</div>
				</div>

				{/* Interiors and Flooring skeleton */}
				<div className="grid grid-cols-1 @4xl:grid-cols-2 gap-8 mb-8 px-2 @4xl:px-0">
					<div className="space-y-4">
						<SkeletonLoader height="24px" width="80px" />
						<div className="space-y-2">
							{Array.from({ length: 3 }, (_, index) => (
								<SkeletonLoader key={index} height="16px" width={`${[65, 80, 70][index]}%`} />
							))}
						</div>
					</div>
					<div className="space-y-4">
						<SkeletonLoader height="24px" width="80px" />
						<div className="space-y-2">
							{Array.from({ length: 3 }, (_, index) => (
								<SkeletonLoader key={index} height="16px" width={`${[60, 75, 85][index]}%`} />
							))}
						</div>
					</div>
				</div>

				{/* Sub Units skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="120px" className="mb-4" />
					<div className="grid grid-cols-1 @4xl:grid-cols-2 gap-4">
						{[...Array(2)].map((_, index) => (
							<div key={index} className="border border-gray-200 rounded-lg p-4">
								<SkeletonLoader height="20px" width="60%" className="mb-2" />
								<SkeletonLoader height="16px" width="40%" className="mb-2" />
								<SkeletonLoader height="16px" width="50%" />
							</div>
						))}
					</div>
				</div>

				{/* Nearby Places Map skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="120px" className="mb-4" />
					<SkeletonLoader height="400px" width="100%" />
				</div>
			</div>

			{/* Nearby Properties skeleton */}
			<div className="px-0 @4xl:px-4">
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="160px" className="mb-4" />
					<div className="flex gap-4 overflow-hidden">
						{[...Array(3)].map((_, index) => (
							<div key={index} className="flex-shrink-0 w-80">
								<div className="border border-gray-200 rounded-lg p-4">
									<SkeletonLoader height="200px" width="100%" className="mb-4" />
									<SkeletonLoader height="20px" width="60%" className="mb-2" />
									<SkeletonLoader height="16px" width="80%" className="mb-2" />
									<div className="flex gap-2">
										<SkeletonLoader height="16px" width="40px" />
										<SkeletonLoader height="16px" width="40px" />
										<SkeletonLoader height="16px" width="40px" />
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Nearby Projects skeleton */}
				<div className="mb-8 px-2 @4xl:px-0">
					<SkeletonLoader height="24px" width="140px" className="mb-4" />
					<div className="flex gap-4 overflow-hidden">
						{[...Array(3)].map((_, index) => (
							<div key={index} className="flex-shrink-0 w-80">
								<div className="border border-gray-200 rounded-lg p-4">
									<SkeletonLoader height="200px" width="100%" className="mb-4" />
									<SkeletonLoader height="20px" width="60%" className="mb-2" />
									<SkeletonLoader height="16px" width="80%" className="mb-2" />
									<div className="flex gap-2">
										<SkeletonLoader height="16px" width="40px" />
										<SkeletonLoader height="16px" width="40px" />
										<SkeletonLoader height="16px" width="40px" />
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Disclaimer skeleton */}
				<div className="px-2 @4xl:px-0 py-1 border-t border-gray-200">
					<SkeletonLoader height="12px" width="100%" className="mb-1" />
					<SkeletonLoader height="12px" width="90%" className="mb-1" />
					<SkeletonLoader height="12px" width="95%" />
				</div>
			</div>
		</div>
	);
}
